import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";

function ViewStudentMarks() {
  const [testNames, setTestNames] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [testName, setTestName] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [subject, setSubject] = useState("");
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showExportOptions, setShowExportOptions] = useState(false);

  // Fetch initial exam names where marks exist
  useEffect(() => {
    const fetchExamData = async () => {
      setLoading(true);
      setError("");
      try {
        const authToken = localStorage.getItem("authToken");
        const response = await axios.get(
          "https://api.uniums.com/api/exam/get-exams-with-marks",
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        setTestNames(response.data.examNames || []);
      } catch (error) {
        console.error("Error fetching exam data:", error);
        setError("Failed to load exam data");
        toast.error("Failed to load exam data");
      } finally {
        setLoading(false);
      }
    };

    fetchExamData();
  }, []);

  // Fetch classes when exam is selected
  useEffect(() => {
    if (!testName) {
      setClasses([]);
      setSelectedClass("");
      return;
    }

    const fetchClasses = async () => {
      setLoading(true);
      setError("");
      try {
        const authToken = localStorage.getItem("authToken");
        const response = await axios.get(
          `https://api.uniums.com/api/exam/get-classes-for-exam?examName=${encodeURIComponent(testName)}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        setClasses(response.data.classes || []);
      } catch (error) {
        console.error("Error fetching classes:", error);
        setError("Failed to load classes");
        toast.error("Failed to load classes");
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, [testName]);

  // Fetch subjects when class is selected
  useEffect(() => {
    if (!testName || !selectedClass) {
      setSubjects([]);
      setSubject("");
      return;
    }

    const fetchSubjects = async () => {
      setLoading(true);
      setError("");
      try {
        const authToken = localStorage.getItem("authToken");
        const response = await axios.get(
          `https://api.uniums.com/api/exam/get-subjects-for-exam-class?examName=${encodeURIComponent(testName)}&className=${encodeURIComponent(selectedClass)}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        setSubjects(response.data.subjects || []);
      } catch (error) {
        console.error("Error fetching subjects:", error);
        setError("Failed to load subjects");
        toast.error("Failed to load subjects");
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [testName, selectedClass]);

  // Fetch student marks when all dropdowns are selected
  useEffect(() => {
    if (!testName || !selectedClass || !subject) {
      setStudents([]);
      setFilteredStudents([]);
      return;
    }

    const fetchStudentMarks = async () => {
      setLoading(true);
      setError("");
      try {
        const authToken = localStorage.getItem("authToken");
        const response = await axios.get(
          `https://api.uniums.com/api/exam/get-student-marks?examName=${encodeURIComponent(testName)}&className=${encodeURIComponent(selectedClass)}&subject=${encodeURIComponent(subject)}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        setStudents(response.data.students || []);
        setFilteredStudents(response.data.students || []);
      } catch (error) {
        console.error("Error fetching student marks:", error);
        setError("Failed to load student marks");
        toast.error("Failed to load student marks");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentMarks();
  }, [testName, selectedClass, subject]);

  // Handle search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = students.filter(
      student =>
        student.name.toLowerCase().includes(searchTermLower) ||
        student.employeeCode.toLowerCase().includes(searchTermLower)
    );
    setFilteredStudents(filtered);
  }, [searchTerm, students]);

  const handleTestChange = (e) => {
    setTestName(e.target.value);
    setSelectedClass("");
    setSubject("");
    setStudents([]);
    setFilteredStudents([]);
    setSearchTerm("");
  };

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
    setSubject("");
    setStudents([]);
    setFilteredStudents([]);
    setSearchTerm("");
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };



  const handleExportPDF = () => {
    const element = document.querySelector(".marks-table");
    html2pdf()
      .from(element)
      .save(`Student_Marks_${testName}_${selectedClass}_${subject}.pdf`);
  };

  const handleExportExcel = () => {
    const ws = XLSX.utils.table_to_sheet(document.querySelector(".marks-table"));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Student Marks");
    XLSX.writeFile(wb, `Student_Marks_${testName}_${selectedClass}_${subject}.xlsx`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".export-container")) {
        setShowExportOptions(false);
      }
    };

    if (showExportOptions) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showExportOptions]);


  return (
    <div className="student-upload-container">
      <h1>View Student Marks</h1>

      <div className="dropdown-container">
        <select onChange={handleTestChange} value={testName}>
          <option value="">Select Test Name</option>
          {testNames.map((name, index) => (
            <option key={index} value={name}>
              {name}
            </option>
          ))}
        </select>

        <select
          onChange={handleClassChange}
          value={selectedClass}
          disabled={!testName}
        >
          <option value="">Select Class</option>
          {classes.map((cls, index) => (
            <option key={index} value={cls}>
              {cls}
            </option>
          ))}
        </select>

        <select
          onChange={(e) => setSubject(e.target.value)}
          value={subject}
          disabled={!selectedClass}
        >
          <option value="">Select Subject</option>
          {subjects.map((subj, index) => (
            <option key={index} value={subj}>
              {subj}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="error">{error}</p>}
      {/* {loading && <p className="loading">Loading...</p>} */}
      {loading && <Loader />}

      {testName && selectedClass && subject && students.length > 0 && (
        <div className="search-bar search-bar-marks">
          <input
            type="text"
            placeholder="Search by name or user code..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-box"
          />
        </div>
      )}

      <div className="mark_upload_table_main">
        {testName && selectedClass && subject && filteredStudents.length > 0 && (
          <>
            <div className="export-container">
              <button
                className="export-button"
                onClick={() => setShowExportOptions(!showExportOptions)}
              >
                Export
              </button>
              {showExportOptions && (
                <div className="export-options">
                  <button onClick={handleExportPDF}>Export as PDF</button>
                  <button onClick={handleExportExcel}>Export as Excel</button>
                </div>
              )}
            </div>
            <div className="mark_upload_table">
              <table className="marks-table">
                <thead>
                  <tr>
                    <th>Sl No</th>
                    <th>Name</th>
                    <th>User Code</th>
                    <th>Subject</th>
                    <th>Obtained Marks</th>
                    <th>Total Marks</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student, index) => (
                    <tr key={student.employeeCode}>
                      <td>{index + 1}</td>
                      <td>{student.name}</td>
                      <td>{student.employeeCode}</td>
                      <td>{subject}</td>
                      <td>{student.obtainedMarks}</td>
                      <td>{student.totalMarks}</td>
                      <td>{student.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {testName && selectedClass && subject && filteredStudents.length === 0 && (
          <p className="no-results">No students found matching your search.</p>
        )}
      </div>
      <ToastContainer />
    </div>
  );
}

export default ViewStudentMarks;